import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import bg from '../assets/loginBG.png';
import logo from '../assets/logo2.png';
import userIcon from '../assets/icons/username.svg';
import passIcon from '../assets/icons/password.svg';

const Login = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(false);
  const [message, setMessage] = useState(null);
  const navigate = useNavigate();

  // Automatically redirect if the user is already logged in
  useEffect(() => {
    const storedUser =
      localStorage.getItem('username') || sessionStorage.getItem('username');
    if (storedUser) {
      setUsername(storedUser);
      setRemember(!!localStorage.getItem('username'));
      navigate('/dashboard/overview');
    }
  }, [navigate]);

  useEffect(() => {
    if (message) {
      const timeout = setTimeout(() => {
        setMessage(null);
        if (message.type === 'success') {
          navigate('/dashboard/overview');
        }
      }, 2000);
      return () => clearTimeout(timeout);
    }
  }, [message, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch('/login.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password })
      });
      const data = await res.json();
      if (data.success) {
        if (remember) {
          localStorage.setItem('username', username);
          sessionStorage.removeItem('username');
        } else {
          sessionStorage.setItem('username', username);
          localStorage.removeItem('username');
        }
        setMessage({ type: 'success', text: `Welcome ${data.name}!` });
      } else {
        setMessage({ type: 'error', text: data.message || 'Invalid credentials' });
      }
    } catch {
      setMessage({ type: 'error', text: 'Server error' });
    }
  };

  return (
    <div
      className="relative min-h-screen flex items-center justify-center overflow-hidden bg-cover bg-center"
      style={{ backgroundImage: `url(${bg})` }}
    >
      <div className="absolute inset-0 bg-gradient-to-br from-black via-gray-900 to-gray-800 opacity-70" />
      <motion.div
        className="absolute -left-40 -top-40 w-[36rem] h-[36rem] rounded-full bg-primary/30 blur-3xl"
        animate={{ x: [0, 100, 0], y: [0, 100, 0] }}
        transition={{ repeat: Infinity, duration: 30, ease: 'linear' }}
      />
      <motion.div
        className="absolute -right-40 bottom-0 w-[30rem] h-[30rem] rounded-full bg-indigo-500/20 blur-3xl"
        animate={{ x: [0, -100, 0], y: [0, -100, 0] }}
        transition={{ repeat: Infinity, duration: 25, ease: 'linear' }}
      />
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={
          message?.type === 'error'
            ? { opacity: 1, y: 0, x: [0, -10, 10, -10, 10, 0] }
            : { opacity: 1, y: 0 }
        }
        className="relative z-10 w-11/12 sm:w-[50rem] bg-[#1B222C]/90 backdrop-blur-lg border border-white/10 rounded-xl shadow-2xl p-8 md:flex"
      >
        <div className="md:w-1/2">
          <img src={logo} alt="Logo" className="w-32 mb-6 mx-auto" />
          <form onSubmit={handleSubmit} className="space-y-4">
          <div className="relative">
            <img src={userIcon} alt="" className="absolute left-3 top-1/2 -translate-y-1/2 w-5 opacity-50" />
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Username"
              className="pl-10 pr-3 py-2 w-full rounded bg-transparent border border-gray-600 focus:border-primary focus:outline-none transition"
            />
          </div>
          <div className="relative">
            <img src={passIcon} alt="" className="absolute left-3 top-1/2 -translate-y-1/2 w-5 opacity-50" />
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              className="pl-10 pr-3 py-2 w-full rounded bg-transparent border border-gray-600 focus:border-primary focus:outline-none transition"
            />
          </div>
          <div className="flex items-center text-sm">
            <input
              id="remember"
              type="checkbox"
              checked={remember}
              onChange={(e) => setRemember(e.target.checked)}
              className="mr-2 rounded bg-gray-800 border-gray-600 focus:ring-primary"
            />
            <label htmlFor="remember">Remember me</label>
          </div>
          <button
            type="submit"
            className="w-full bg-primary hover:bg-red-700 transition text-white py-2 rounded"
          >
            Login
          </button>
          </form>
        </div>
        <div className="hidden md:block md:w-1/2 md:pl-6 text-white">
          <h2 className="text-2xl font-bold mb-4">Hello!</h2>
          <p className="text-sm leading-relaxed whitespace-pre-line">
            {`Welcome to V SENSE
Your AI-powered partner in predictive maintenance.

V SENSE is an advanced vibration monitoring platform designed to help you track the health of your machines and equipment in real-time. Powered by smart analytics and machine learning, V SENSE enables early detection of anomalies, reduces downtime, and improves operational efficiency.

Stay informed. Stay ahead.`}
          </p>
        </div>
      </motion.div>
      {message && (
        <motion.div
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: 50 }}
          className={`fixed top-4 right-4 px-4 py-2 rounded shadow-lg text-white ${
            message.type === 'success' ? 'bg-green-600' : 'bg-red-600'
          }`}
        >
          {message.text}
        </motion.div>
      )}
    </div>
  );
};

export default Login;
